import { Session, User } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react-native';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import { supabase } from '../core/config/supabase';
import { logger } from '@/src/core/utils';
import { isAuthRateLimitError } from '@/src/core/utils/authErrors';
import { createSignOutHooks, SignOutHook } from '@/src/core/utils/signOutHooks';

function getAuthRedirectUrl(path: string): string {
  if (Platform.OS === 'web') {
    // On web, use the current origin so it works on any deployment (localhost, Vercel, etc.)
    return `${window.location.origin}/${path}`;
  }
  // On mobile, use the deep link scheme
  return `tournaly://${path}`;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  /** Runs `hook` right before signing out, while the session is still valid. Returns an unregister function. */
  registerBeforeSignOut: (hook: SignOutHook) => () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth throttling (429 / "after N seconds") is user-driven and handled in the UI — not a Sentry error.
function logAuthError(message: string, error: unknown) {
  if (isAuthRateLimitError(error)) {
    logger.warn('Auth', `${message} (rate limited)`, { error });
  } else {
    logger.error('Auth', message, { error });
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const signOutHooks = useRef(createSignOutHooks()).current;

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
      })
      .catch((error) => {
        // Network timeout or connectivity failure (e.g. Supabase unreachable from certain regions).
        // Treat as unauthenticated — the user can still browse public content.
        logger.warn('Auth', 'Failed to get initial session', { error });
      })
      .finally(() => {
        setLoading(false);
      });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      logger.info('Auth', 'Auth state changed', { event: _event });
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        Sentry.setUser({ id: session?.user?.id, email: session?.user?.email });
      } else {
        Sentry.setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: getAuthRedirectUrl('auth/confirm'),
        },
      });

      if (error) {
        logAuthError('Sign up failed', error);
        return { error };
      }

      logger.info('Auth', 'Sign up successful');
      return { error: null };
    } catch (error) {
      if (error instanceof Error && error.name === 'AuthRetryableFetchError') {
        logger.warn('Auth', 'Sign up network error', { error });
      } else {
        logger.error('Auth', 'Sign up exception', { error });
      }
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        logAuthError('Sign in failed', error);
        return { error };
      }

      logger.info('Auth', 'Sign in successful');
      Sentry.setUser({ id: data.user?.id, email: data.user?.email });
      return { error: null };
    } catch (error) {
      if (error instanceof Error && error.name === 'AuthRetryableFetchError') {
        logger.warn('Auth', 'Sign in network error', { error });
      } else {
        logger.error('Auth', 'Sign in exception', { error });
      }
      return { error };
    }
  };

  const signOut = async () => {
    try {
      await signOutHooks.runAll();
      const { error } = await supabase.auth.signOut();
      if (error) {
        logger.error('Auth', 'Sign out failed', { error });
      } else {
        logger.info('Auth', 'Sign out successful');
        Sentry.setUser(null);
      }
    } catch (error) {
      logger.error('Auth', 'Sign out exception', { error });
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getAuthRedirectUrl('auth/reset-password'),
      });

      if (error) {
        logAuthError('Reset password failed', error);
        return { error };
      }

      logger.info('Auth', 'Reset password email sent');
      return { error: null };
    } catch (error) {
      logger.error('Auth', 'Reset password exception', { error });
      return { error };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        signUp,
        signIn,
        signOut,
        resetPassword,
        registerBeforeSignOut: signOutHooks.register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}